import { Navigate, NavLink, Route, Routes } from "react-router-dom"
import { lazy, ReactNode, Suspense, useContext, useEffect, useState } from "react"
import { AuthContext, useModal } from "@surveying-hub-bv/fe-component-library"
import { CreateOrganizationFailure, OrganizationService } from "../services/OrganizationService"
import { LoginForm, SignUpForm } from "../pages/Login"
import { loadingAction } from "../components/Dialogs"
import { AxiosError } from "axios"
import { Result } from "../services/AxiosService"
import { AlertIcon } from "../components/ContentElements"
import { Button } from "../components/Buttons"
import { TextInput } from "../components/Forms"
import { useTranslation } from "react-i18next"
import { LoadingSpinner } from "../components/LoadingSpinners"
import EagerSVGIcon from "../components/EagerSVG"
import { AdminLayout, LoggedOutLayout, UserLayout } from "./Layout"
import { LayoutComponent } from "./layout-components/Layout"
import { ExtendedAuthContextInterface } from "../AuthProvider"
import { GenericErrorBlock } from "../components/ErrorBlocks"
import { MainContentWrapper, PageWrapper } from "./layout-components/ContentWrappers"
import { NotificationBanner } from "./layout-components/NotificationBanner"
import { PricingTable, SubscriptionService } from "../services/SubscriptionService"

// This opens a modal with the login form when the user is not logged in.
const LoginPopupRoute = ({ signup = false }: { signup?: boolean }) => {
    const { showModal, hideModal } = useModal()
    const isLoggedIn = useContext(AuthContext) as ExtendedAuthContextInterface | 'loading' | undefined

    useEffect(() => {
        // Show modal on mount
        showModal(<LoginFormDialog signup={signup} />)
        // Hide modal on unmount
        return () => hideModal()
    }, [showModal, hideModal, signup])

    useEffect(() => {
        // If user is logged in, close the modal
        if (isLoggedIn && isLoggedIn !== 'loading') {
            hideModal()
        }
    }, [isLoggedIn, hideModal])

    return null
}

export const LoginComponent = () => {
    const isLoggedIn = useContext(AuthContext) as ExtendedAuthContextInterface | 'loading' | undefined
    const [selected, setSelected] = useState<PricingTable | undefined>(undefined)
    useEffect(() => {
        let cancelled = false

        const selectedName = process.env.REACT_APP_NOTIFICATION_NETWORK_NAME
        if (selectedName === undefined) return
        async function load() {
            const pricingTables = await SubscriptionService.getPricingTables()
            const result = pricingTables.find(p => p.name === selectedName)

            if (!cancelled) {
                setSelected(result)
            }
        }

        load()

        return () => {
            cancelled = true
        }
    }, [])

    if (isLoggedIn === 'loading') {
        return <LoadingSpinner />
    }

    if (isLoggedIn === undefined) {
        return <> {selected && <NotificationBanner loggedIn={isLoggedIn} selected={selected} />}<LoggedOutRouting /></>
    }

    return <> {selected && <NotificationBanner loggedIn={isLoggedIn} selected={selected} />}<LoggedInRouting loggedIn={isLoggedIn} /></>
}

// Page import map for lazy loading.F
const AdminAccountsPage = lazy(() => import("../pages/accounts/AccountsPage").then(module => ({ default: module.AdminAccountsPage })))
const AdminFeedPage = lazy(() => import("../pages/feed/FeedPage").then(module => ({ default: module.AdminFeedPage })))
const AdminMapPage = lazy(() => import("../pages/map/MapPage").then(module => ({ default: module.AdminMapPage })))
const AdminOrganizationManagementPage = lazy(() => import("../pages/organizationmanagement/OrganizationManagementPage").then(module => ({ default: module.AdminOrganizationManagementPage })))
const AdminProductPage = lazy(() => import("../pages/product/ProductPage").then(module => ({ default: module.AdminProductPage })))
const AdminSettingsPage = lazy(() => import("../pages/adminsettings/AdminSettingsPage").then(module => ({ default: module.AdminSettingsPage })))
const AdminSubscriptionsPage = lazy(() => import("../pages/subscriptions/SubscriptionsPage").then(module => ({ default: module.AdminSubscriptionsPage })))
const AdminUserManagementPage = lazy(() => import("../pages/usermanagement/UserManagementPage").then(module => ({ default: module.AdminUserManagementPage })))
const CheckoutPage = lazy(() => import("../pages/checkout/CheckoutPage").then(module => ({ default: module.CheckoutPage })))
const MarketplacePage = lazy(() => import("../pages/marketplace/MarketplacePage").then(module => ({ default: module.MarketplacePage })))
// const OrganisationCheckoutPage = lazy(() => import("../pages/organisationcheckout/OrganisationCheckoutPage").then(module => ({ default: module.OrganisationCheckoutPage })))
// const OrganisationSettingsPage = lazy(() => import("../pages/organisationsettings/OrganisationSettingsPage").then(module => ({ default: module.OrganisationSettingsPage })))
// const OrganizationAccountsPage = lazy(() => import("../pages/accounts/AccountsPage").then(module => ({ default: module.OrganizationAccountsPage })))
// const OrganizationAdminProductPage = lazy(() => import("../pages/product/ProductPage").then(module => ({ default: module.OrganizationAdminProductPage })))
// const OrganizationFeedPage = lazy(() => import("../pages/feed/FeedPage").then(module => ({ default: module.OrganizationFeedPage })))
// const OrganizationMapPage = lazy(() => import("../pages/map/MapPage").then(module => ({ default: module.OrganizationMapPage })))
// const OrganizationMemberSubscriptionsPage = lazy(() => import("../pages/subscriptions/SubscriptionsPage").then(module => ({ default: module.OrganizationMemberSubscriptionsPage })))
const PersonalSettingsPage = lazy(() => import("../pages/personalsettings/PersonalSettingsPage").then(module => ({ default: module.PersonalSettingsPage })))
const PseudoNetworkPage = lazy(() => import("../pages/pseudofeed/NetworkPage").then(module => ({ default: module.PseudoNetworkPage })))
const UserAccountsPage = lazy(() => import("../pages/accounts/AccountsPage").then(module => ({ default: module.UserAccountsPage })))
const UserCreditsPage = lazy(() => import("../pages/credits/CreditsPage").then(module => ({ default: module.CreditsPage })))
const UserDashboardPage = lazy(() => import("../pages/dashboard/DashboardPage").then(module => ({ default: module.LoggedInDashboardPage })))
const UserLoggedOutDashboardPage = lazy(() => import("../pages/dashboard/DashboardPage").then(module => ({ default: module.LoggedOutDashboardPage })))
const UserMapPage = lazy(() => import("../pages/map/MapPage").then(module => ({ default: module.UserMapPage })))
const UserSubscriptionsPage = lazy(() => import("../pages/subscriptions/SubscriptionsPage").then(module => ({ default: module.UserSubscriptionsPage })))


const LoggedInRouting = ({ loggedIn }: { loggedIn: ExtendedAuthContextInterface }) => {
    return (
        <Suspense fallback={
            <LoadingSpinner />
        } >
            <Routes>
                <Route path="/dashboard" element={<UserRoute loggedIn={loggedIn} Component={UserDashboardPage} url="dashboard" />} />
                <Route path="/map" element={<UserRoute loggedIn={loggedIn} Component={UserMapPage} url="map" />} />
                <Route path="/accounts" element={<UserRoute loggedIn={loggedIn} Component={UserAccountsPage} url="map" />} />
                <Route path="/credits" element={<UserRoute loggedIn={loggedIn} Component={UserCreditsPage} url="credits" />} />
                <Route path="/subscriptions" element={<UserRoute loggedIn={loggedIn} Component={UserSubscriptionsPage} url="map" />} />
                <Route path="/settings" element={<UserRoute loggedIn={loggedIn} Component={PersonalSettingsPage} url="settings" />} />
                <Route path="/settings/:settingsPage?" element={<UserRoute loggedIn={loggedIn} Component={PersonalSettingsPage} url="settings" />} />

                {/* <Route path="/org/:organization/map" element={<OrganizationMemberRoute loggedIn={loggedIn} Component={OrganizationMapPage} url='map' />} />
                <Route path="/org/:organization/accounts" element={<OrganizationMemberRoute loggedIn={loggedIn} Component={OrganizationAccountsPage} url='accounts' />} />
                <Route path="/org/:organization/subscriptions" element={<OrganizationMemberRoute loggedIn={loggedIn} Component={OrganizationMemberSubscriptionsPage} url='subscriptions' />} />

                <Route path="/org/:organization/feed" element={<OrganizationAdminRoute loggedIn={loggedIn} Component={OrganizationFeedPage} url='feed' />} />
                <Route path="/org/:organization/feed/:networkId" element={<OrganizationAdminRoute loggedIn={loggedIn} Component={OrganizationFeedPage} url='feed' />} />
                <Route path="/org/:organization/settings/:settingsPage?" element={<OrganizationAdminRoute loggedIn={loggedIn} Component={OrganisationSettingsPage} url='organisation-settings' />} />
                <Route path="/org/:organization/products" element={<OrganizationAdminRoute loggedIn={loggedIn} Component={OrganizationAdminProductPage} url='products' />} />
                <Route path="/org/:organization/checkout" element={<OrganizationAdminRoute loggedIn={loggedIn} Component={OrganisationCheckoutPage} url='checkout' />} />
                <Route path="/org/:organization/*" element={<OrganizationMemberRedirectRoute />} /> */}

                <Route path="/admin/map" element={<AdminRoute loggedIn={loggedIn} Component={AdminMapPage} url='map' />} />
                <Route path="/admin/products/:productsPage?" element={<AdminRoute loggedIn={loggedIn} Component={AdminProductPage} url='products' />} />
                <Route path="/admin/feed" element={<AdminRoute loggedIn={loggedIn} Component={AdminFeedPage} url='feed' />} />
                <Route path="/admin/feed/:networkId" element={<AdminRoute loggedIn={loggedIn} Component={AdminFeedPage} url='feed' />} />
                <Route path="/admin/users" element={<AdminRoute loggedIn={loggedIn} Component={AdminUserManagementPage} url='users' />} />
                <Route path="/admin/subscriptions" element={<AdminRoute loggedIn={loggedIn} Component={AdminSubscriptionsPage} url='subscriptions' />} />
                <Route path="/admin/accounts" element={<AdminRoute loggedIn={loggedIn} Component={AdminAccountsPage} url='accounts' />} />
                <Route path="/admin/organizations" element={<AdminRoute loggedIn={loggedIn} Component={AdminOrganizationManagementPage} url='organizations' />} />
                <Route path="/admin/networks" element={<AdminRoute loggedIn={loggedIn} Component={PseudoNetworkPage} url='networks' />} />
                <Route path="/admin/settings/:settingsPage?" element={<AdminRoute loggedIn={loggedIn} Component={(props) => <AdminSettingsPage {...props} parameter="settings" />} url='settings' />} />
                <Route path="/admin/networks/:networkId" element={<AdminRoute loggedIn={loggedIn} Component={PseudoNetworkPage} url='networks' />} />
                <Route path="/admin/*" element={<Navigate to="/admin/map" replace />} />

                <Route path="/checkout" element={<UserRoute loggedIn={loggedIn} Component={CheckoutPage} url="checkout" />} />

                {pages(loggedIn)}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </Suspense>
    )
}

const pages = (loggedIn?: ExtendedAuthContextInterface) => <>
    <Route path="/dashboard" element={<OptionalLoggedInRoute loggedIn={loggedIn} Component={UserLoggedOutDashboardPage} url="dashboard" />} />
    <Route path="/marketplace" element={<OptionalLoggedInRoute loggedIn={loggedIn} Component={MarketplacePage} url="test" />} />
    {/* <Route path="/networks/:networkId?" element={<OptionalLoggedInRoute loggedIn={loggedIn} Component={NetworkDetailsPage} url="networks" />} /> */}
</>

const LoggedOutRouting = () => <Routes>
    <Route path="/login" element={<LoginPopupRoute />} />
    <Route path="/signup" element={<LoginPopupRoute signup={true}/>} />
    <Route path="/register" element={<LoginPopupRoute signup={true}/>} />

    {pages()}
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
</Routes>

type UserRouteProps = {
    loggedIn: ExtendedAuthContextInterface
    Component: React.FC<{
        loggedIn: ExtendedAuthContextInterface
        Component: LayoutComponent
    }>
    url: string
}
const UserRoute = ({ Component, loggedIn, url }: UserRouteProps) =>
    <Component loggedIn={loggedIn} Component={UserLayout(getNewSelector(loggedIn, "Personal", url), loggedIn)} />

type OptionalLoggedInRouteProps = {
    loggedIn?: ExtendedAuthContextInterface
    Component: React.FC<{
        loggedIn?: ExtendedAuthContextInterface
        Component: LayoutComponent
    }>
    url: string
}
const OptionalLoggedInRoute = ({ Component, loggedIn, url }: OptionalLoggedInRouteProps) => loggedIn !== undefined ?
    <Component loggedIn={loggedIn} Component={UserLayout(loggedIn ? getNewSelector(loggedIn, "Personal", url) : <></>, loggedIn)} /> :
    <Component loggedIn={loggedIn} Component={LoggedOutLayout} />

// type LoggedOutRouteProps = {
//     Component: React.FC<{
//         Component: LayoutComponent
//     }>
// }
// const LoggedOutRoute = ({ Component }: LoggedOutRouteProps) =>
//     <Component Component={LoggedOutLayout} />

// const OrganizationMemberRedirectRoute = () => {
//     const { organization } = useParams()
//     if (organization === undefined) {
//         return <>Something went unexpectedly wrong</>
//     }

//     return <Navigate to={`/org/${organization}/map`} replace />
// }

// type OrganizationMemberRouteProps = {
//     loggedIn: ExtendedAuthContextInterface
//     Component: React.FC<{
//         loggedIn: ExtendedAuthContextInterface
//         parameter: string
//         Component: LayoutComponent
//     }>
//     url: string
// }
// const OrganizationMemberRoute = ({ Component, loggedIn, url }: OrganizationMemberRouteProps) => {
//     const { organization } = useParams()

//     if (loggedIn.teams === 'loading') {
//         return <LoadingSpinner />
//     }

//     if (loggedIn.teams === undefined || organization === undefined) {
//         return <>Something went unexpectely wrong</>
//     }

//     const roleInfo = loggedIn.teams.find(team => team.url === organization)
//     if (roleInfo === undefined) {
//         return <>You dont have access to this organization</>
//     }

//     const selector = getSelector(loggedIn, roleInfo.name, url)

//     return <Component loggedIn={loggedIn} parameter={organization} Component={roleInfo.role === 'Admin' ? OrganizationAdminLayout(organization, selector, loggedIn) : OrganizationMemberLayout(organization, selector, loggedIn)} />
// }

// type OrganizationAdminRouteProps = {
//     loggedIn: ExtendedAuthContextInterface
//     Component: React.FC<{
//         loggedIn: ExtendedAuthContextInterface
//         parameter: string
//         Component: LayoutComponent
//     }>
//     url: string
// }


// const OrganizationAdminRoute = ({ Component, loggedIn, url }: OrganizationAdminRouteProps) => {
//     const { organization } = useParams();

//     const isLoading = loggedIn.teams === 'loading';
//     const hasError = loggedIn.teams === undefined || organization === undefined;
//     const roleInfo = loggedIn.teams !== "loading" && loggedIn.teams?.find(team => team.url === organization);
//     const isAdmin = roleInfo !== false && roleInfo?.role === 'Admin';

//     if (isLoading) {
//         return <LoadingSpinner />
//     }

//     if (!roleInfo || !isAdmin) {
//         return <Navigate to={`/org/${organization}/map`} replace />
//     }

//     if (hasError) {
//         return <>Something went unexpectedly wrong</>;
//     }

//     // Render the component only if the user is authorized
//     return (
//         <Component
//             loggedIn={loggedIn}
//             parameter={organization}
//             Component={OrganizationAdminLayout(
//                 organization,
//                 getSelector(loggedIn, roleInfo ? roleInfo.name : '', url),
//                 loggedIn
//             )}
//         />
//     );
// };


type AdminRouteProps = {
    loggedIn: ExtendedAuthContextInterface
    Component: React.FC<{
        loggedIn: ExtendedAuthContextInterface
        Component: LayoutComponent
    }>
    url: string
}
const AdminRoute = ({ Component, loggedIn, url }: AdminRouteProps) => {

    if (loggedIn.isAdmin === 'loading') {
        return <LoadingSpinner />
    }

    if (loggedIn.isAdmin === undefined) {
        return <GenericErrorBlock />
    }

    if (!loggedIn.isAdmin) {
        return <PageWrapper>
            <MainContentWrapper sideMenuIsOpen={false} isMobile={false} isFullScreen={true}>
                <div className="flex flex-col items-center h-full lg:h-[40%] justify-center text-center">
                    <div className="border rounded-[24px] dark:border-darkMode-warning border-opacity-65 p-6 max-w-[600px]">
                        <AlertIcon type="warning" />
                        <h2 className="text-[20px] font-bold my-4">Restricted Access</h2>
                        <p className="mb-6">You do not have the permissions to view this page.</p>

                        <Button label="Back to Dashboard" href="/dashboard" buttonStyle="linkMuted" />
                    </div>
                </div>

            </MainContentWrapper>
        </PageWrapper>
    }

    const selector = getNewSelector(loggedIn, "Admin", url)

    return <Component loggedIn={loggedIn} Component={AdminLayout(selector, loggedIn)} />
}

// const getSelector = (loggedIn: ExtendedAuthContextInterface, prefix: string, currentPage: string) =>
//     loggedIn.teams === 'loading' || loggedIn.isAdmin === 'loading' ? <>Loading</> : <Selector organizations={loggedIn.teams || []} prefix={prefix} currentPage={currentPage} loggedIn={loggedIn} />

// const Selector = ({ organizations, prefix, currentPage, loggedIn }: { prefix: string, organizations: TeamInfo[], currentPage: string, loggedIn: ExtendedAuthContextInterface }) => {
//     const { showModal } = useModal()
//     const { t } = useTranslation()

//     const options = [
//         <Link className="cursor-pointer flex gap-2 items-center" to={`/${currentPage}`}>
//             <div
//                 style={{
//                     background: `linear-gradient(in lch -45deg, ${personalColor1}, ${personalColor2})`,
//                 }}
//                 className="w-4 h-4 rounded-full"
//             ></div>
//             {t("navigation.personal")}
//         </Link>,
//         // Hidden if Rebranded
//         ...(!branding.isRebranded
//             ? organizations.map(organization => {
//                 const { color1, color2 } = generateColors(organization.name)
//                 return (
//                     <Link
//                         key={organization.url}
//                         className="cursor-pointer flex gap-2 items-center"
//                         to={`/org/${organization.url}/${currentPage}`}
//                     >
//                         <div
//                             style={{
//                                 background: `linear-gradient(in lch -45deg, ${color1}, ${color2})`,
//                             }}
//                             className="w-4 h-4 rounded-full"
//                         ></div>
//                         {organization.name}
//                     </Link>
//                 )
//             })
//             : []
//         ),
//         ...(loggedIn.isAdmin ? [
//             <Link className="cursor-pointer flex gap-2 items-center" to={`/admin/${currentPage}`}>
//                 <div
//                     style={{
//                         background: `linear-gradient(in lch -45deg, ${adminColor1}, ${adminColor2})`,
//                     }}
//                     className="w-4 h-4 rounded-full"
//                 ></div>
//                 {t("navigation.admin")}
//             </Link>
//         ] : []),
//         // Hidden if Rebranded
//         ...(!branding.isRebranded
//             ? [
//                 <button onClick={() => showModal(<CreateNewOrganizationDialog loggedIn={loggedIn} />)}>
//                     ➕ Create organization
//                 </button>
//             ]
//             : []
//         )
//     ]


//     const { color1: currentColor1, color2: currentColor2 } = generateColors(prefix)
//     return <div>
//         <CustomSelect options={options} currentPage={<><div style={{
//             background: `linear-gradient(in lch -45deg, ${currentColor1}, ${currentColor2})`,
//         }} className="w-4 h-4 rounded-full"></div>{prefix === "Personal" || prefix === "Admin" ? t(`navigation.${prefix.toLowerCase()}`) : prefix}</>} /></div> // Translates 'Personal' and 'Admin' and leaves the Org names as is.
// }


const getNewSelector = (loggedIn: ExtendedAuthContextInterface, prefix: string, currentPage: string) =>
    loggedIn.isAdmin === 'loading' ? <>Loading</> : <NewSelector prefix={prefix} currentPage={currentPage} loggedIn={loggedIn} />

const NewSelector = ({ prefix, currentPage, loggedIn }: { prefix: string, currentPage: string, loggedIn: ExtendedAuthContextInterface }) => {
    const { t } = useTranslation()

    const options = [
        ...(loggedIn.isAdmin ? [
            <NavLink className={({ isActive }) => `${isActive ? "dark:text-darkMode-primary font-bold" : ""} cursor-pointer flex mb-1 p-2 hover:dark:bg-darkMode-layer1color transition-all duration-300 rounded-md`} to={`/admin/${currentPage}`} key="admin">
                <span className="flex items-center gap-x-2"><EagerSVGIcon name="RtkSignalIcon" width={20} height={20} /> {t("navigation.admin")}</span>
            </NavLink>
        ] : []),
        <NavLink className={({ isActive }) => `${isActive ? "dark:text-darkMode-primary font-bold" : ""} cursor-pointer flex p-2 hover:dark:bg-darkMode-layer1color transition-all duration-300 rounded-md`} to={`/${currentPage}`} key="personal">
            <span className="flex items-center gap-x-2"><EagerSVGIcon name="UserIcon" width={20} height={20} /> {t("navigation.personal")}</span>
        </NavLink>,
    ]

    return <div className="p-1">{options}</div>
}

// function generateColors(input: string) {
//     const seed = stringToSeed(input)
//     const angle1 = seededRandom(seed, 0, 360)
//     const angle2 = seededRandom(seed + 1, 0, 120)
//     const color1 = `hsl(${angle1}, 100%, 50%)`
//     const angle2Mod = (angle1 + angle2 + 120) % 360
//     const color2 = `hsl(${angle2Mod}, 100%, 50%)`
//     return { color1, color2 }
// }

// const stringToSeed = (str: string): number => {
//     let hash = 0

//     if (str.length === 0) return hash

//     for (let i = 0; i < str.length; i++) {
//         const char = str.charCodeAt(i)
//         hash = ((hash << 5) - hash) + char
//         hash = hash & hash
//     }

//     return hash
// }

// const seededRandom = (seed: number, min: number, max: number) => {
//     const x = Math.sin(seed) * 10000
//     return Math.floor((x - Math.floor(x)) * (max - min) + min)
// }
// const { color1: personalColor1, color2: personalColor2 } = generateColors("Personal")
// const { color1: adminColor1, color2: adminColor2 } = generateColors("Admin")


// LOGIN FORM POPUP
export const LoginFormDialog = ({ signup = false }: { signup?: boolean }) => {
    const [authTypeRegister, setAuthTypeRegister] = useState(signup)

    const authSwitch = () => {
        setAuthTypeRegister(!authTypeRegister)
    }

    return (
        <div className="modal-content">
            {authTypeRegister ? <SignUpForm logInLink={"login"} authSwitch={authSwitch} /> : <LoginForm signUpLink={"signup"} authSwitch={authSwitch} />}
        </div>
    )
}


type OrganizationCreatedDialogProps = {
    organizationName: string
    organizationUrl: string
}
const OrganizationCreatedDialog = ({ organizationName, organizationUrl }: OrganizationCreatedDialogProps) => {
    const { hideModal } = useModal()

    return <div className="modal-content">
        <AlertIcon type="success" />
        <h3 className="modal-title text-center">Organisation Created</h3>
        <p className="text-gray-400 text-center mb-2">Your organisation, <span className="text-primaryGreen">{organizationName}</span>, has been created.</p>
        <div className="modal-footer-actions">
            <Button label="Visit Organisation" onClick={hideModal} size="large" alwaysFullWidth />
        </div>
    </div>
}

type OrganizationCreationFailedDialogProps = {
    organizationName: string
    error: CreateOrganizationFailure,
    retry: ReactNode
}
const OrganizationCreationFailedDialogMessages: { [key in CreateOrganizationFailure]: string } = {
    MaximumOrganizationCreated: "You have reached the maximum amount of organizations you can create.",
    CanNotBeEmpty: "The organization name can not be empty.",
    AlreadyTaken: "This name is already taken.",
    MinimumCharactersNotMet: "The organization name has to be at least 3 characters long. Excluding special characters."
}
const OrganizationCreationFailedDialog = ({ organizationName, error, retry }: OrganizationCreationFailedDialogProps) => {
    const { showModal, hideModal } = useModal()

    return <div className="modal-content">
        <AlertIcon type="error" />
        <h3 className="modal-title text-center">Organisation Creation Failed</h3>
        <p className="text-gray-400 text-center">An error occurred while creating organisation.</p>
        <p className="mb-2 text-center">{OrganizationCreationFailedDialogMessages[error]}</p>
        <div className="modal-footer-actions">
            <Button label="Close" onClick={hideModal} buttonStyle="muted" size="large" />
            <Button label="Try Again" onClick={() => showModal(<>{retry}</>)} buttonStyle="secondary" size="large" />
        </div>
    </div>
}

export type CreateNewOrganizationDialogProps = {
    init?: string
    loggedIn: ExtendedAuthContextInterface
    onClose?: (successfullResult: string) => void
}
export const CreateNewOrganizationDialog = ({ loggedIn, init, onClose }: CreateNewOrganizationDialogProps) => {
    const { showModal, hideModal } = useModal()
    const [organizationName, setOrganizationName] = useState(init || '')
    const onCloseFunction = (result: Result<string, any>) => result.success && onClose !== undefined && onClose(result.value)

    return <div className="modal-content text-center">
        <h3 className="modal-title">Create Your Own<br /><span className="text-[24px] font-semibold gradient-text">Organisation [Beta]</span></h3>
        <p>Create your own organisation and use our advanced user management system for customer and colleague management</p>
        <ul>
            <li>• Connect and manage any RTK network</li>
            <li>• Set time limited access </li>
            <li>• Invite new members</li>
        </ul>
        <p>Interested in trying out our beta version? Create an organization and we will get in contact with you</p>

        <TextInput
            type="text"
            label="Enter name for your organization"
            name="organizationName"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            maxLength={32}
            size="large"
            placeholder="Enter name for your organization"
        />

        <div className="modal-footer-actions">
            <Button label={"Cancel"} buttonStyle="muted" size="large" onClick={() => {
                hideModal()
            }} />
            <Button label="Create Organisation" size="large" onClick={async () => loadingAction(showModal,
                `Creating a new organization: ${organizationName}`,
                OrganizationService.createOrganization(await loggedIn.getToken(), organizationName),
                (result) => {
                    if (result.success) {
                        window.dataLayer.push({ event: 'organizationCreated', organizationName })
                        loggedIn.refetch()
                        return <OrganizationCreatedDialog organizationName={organizationName} organizationUrl={result.value} />
                    } else {
                        return <OrganizationCreationFailedDialog organizationName={organizationName} error={result.error} retry={<CreateNewOrganizationDialog loggedIn={loggedIn} init={organizationName} />} />
                    }
                },
                (e) => {
                    const error = e as AxiosError<{ reason: string }>
                    return <ErrorScreen error={error} organizationName={organizationName} loggedIn={loggedIn} />
                },
                onCloseFunction
            )
            } />

        </div>
        <p className="text-[#606060] text-xs">Note: Access to our beta version is not guaranteed. RTK FIX reserves the right to deny access </p>
    </div>
}

const ErrorScreen = ({ error, organizationName, loggedIn }: { organizationName: string, loggedIn: ExtendedAuthContextInterface, error: AxiosError<{ reason: string }> }) => {
    const { showModal, hideModal } = useModal()
    return <div className="modal-content">
        <AlertIcon type="warning" />
        <h3 className="modal-title text-center">Organisation Creation Failed</h3>
        <p className="text-gray-400 text-center mb-2">An error occurred while creating organisation.</p>
        {error.response?.status === 400 ? <p className="text-warning text-center">{error.response?.data.reason}</p> : undefined}
        <div className="modal-footer-actions">
            <Button label="Close" onClick={hideModal} buttonStyle="muted" size="large" />
            <Button label="Try Again" onClick={() => showModal(<CreateNewOrganizationDialog init={organizationName} loggedIn={loggedIn} />)} buttonStyle="secondary" size="large" />
        </div>
    </div>
}
